import flatpickr from 'flatpickr';
import locales from 'flatpickr/dist/l10n';

class Iibe {
  constructor(options = {}) {
    this.config = {
      moduleList: document.querySelectorAll('[data-module="Iibe"]'),
      formModule: document.querySelector('#iibeFormModal'),
      today: new Date(),
      timeoutDelay: 500,
      ...options
    };

    this.timeout = null;
  }

  /**
   * When a book button is clicked
   * Get values from button data attributes And fill form with these values
   * Opening form modal is handled with Bootstrap
   * @param e
   */
  onButtonClick(e) {
    const { formModule } = this.config;

    e.preventDefault();

    if (!formModule) {
      return;
    }

    const btn = e.target;
    const roomIdField = formModule.querySelector('[name="roomId"]');
    const dateField = formModule.querySelector('[name="date"]');
    const nightsField = formModule.querySelector('[name="nights"]');
    const roomField = formModule.querySelector('[name="room"]');
    const priceField = formModule.querySelector('[name="price"]');

    if (!roomIdField || !dateField || !nightsField || !roomField || !priceField) {
      return;
    }

    roomIdField.value = btn.dataset.iibeRoomId;
    dateField.value = btn.dataset.iibeDate;
    nightsField.value = btn.dataset.iibeNights;
    roomField.value = btn.dataset.iibeRoom;
    priceField.value = btn.dataset.iibePrice;
  }

  /**
   * When API call is successful
   * HTML is returned which contains book button
   * Always remove old event listeners and create new ones to trigger onButtonClick
   * @param module
   */
  createButtonListeners(module) {
    module.querySelectorAll('[data-module="IibeBookButton"]').forEach(button => {
      button.removeEventListener('click', this.onButtonClick.bind(this));
    });

    setTimeout(() => {
      module.querySelectorAll('[data-module="IibeBookButton"]').forEach(button => {
        button.addEventListener('click', this.onButtonClick.bind(this));
      });
    });
  }

  /**
   * Remove load state from module
   * Used when API call failed or was successful
   * @param module
   */
  removeLoader(module) {
    setTimeout(() => {
      module.classList.remove('is-loading');
    }, this.config.timeoutDelay);
  }

  /**
   * Check if given date equals today
   * @param date
   * @returns {boolean}
   */
  isToday(date) {
    if (!date) return false;
    const { today } = this.config;
    return date.getDate() === today.getDate();
  }

  /**
   * Get room options based on selected date and nights
   * Make API call and update DOM with new room options HTML
   * Called on first page load and when date/nights has been changed
   * @param module
   */
  getOptions(module) {
    const { roomId } = module.dataset;
    const dateValue = module.querySelector('[data-module="IibeDateValue"]');
    const nightsValue = module.querySelector('[data-module="IibeNightsValue"]');
    const resultsContainer = module.querySelector('[data-module="IibeResults"]');

    if (!roomId || !dateValue || !nightsValue || !resultsContainer) return;

    const url = `/api/v1/hotel/day?room=${roomId}&days=${nightsValue.value}&date=${
      dateValue.value
    }`;

    module.classList.remove('is-error');
    module.classList.add('is-loading');

    fetch(url, { method: 'GET' })
      .then(response => {
        if (response.ok) {
          return response.text();
        }
        throw new Error(response.statusText);
      })
      .then(html => {
        resultsContainer.innerHTML = html;
        this.createButtonListeners(module);
        this.removeLoader(module);
        module.classList.add('is-success');
      })
      .catch(() => {
        resultsContainer.innerHTML = `
            <p class="error">
                ${module.dataset.apiErrorMessage || 'No rooms available.'}
            </p>`;

        this.removeLoader(module);
        module.classList.remove('is-success');
        module.classList.add('is-error');
      });
  }

  /**
   * Triggers getOptions after a configured delay
   * This to prevent overloading the API with every date or nights change
   * For better performance and UX
   * @param module
   */
  onChangeDebounce(module) {
    const { timeoutDelay } = this.config;

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.getOptions(module);
    }, timeoutDelay);
  }

  init() {
    const { moduleList, today } = this.config;
    const htmlLang = document.getElementsByTagName('html')[0].getAttribute('lang');
    const locale = htmlLang || navigator.language;
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    moduleList.forEach(module => {
      const dateIncrementer = module.querySelector('[data-module="IibeDateIncrementer"]');
      const dateDecrementer = module.querySelector('[data-module="IibeDateDecrementer"]');
      const dateValue = module.querySelector('[data-module="IibeDateValue"]');
      const nightsIncrementer = module.querySelector('[data-module="IibeNightsIncrementer"]');
      const nightsDecrementer = module.querySelector('[data-module="IibeNightsDecrementer"]');
      const nightsValue = module.querySelector('[data-module="IibeNightsValue"]');

      if (
        !dateIncrementer ||
        !dateDecrementer ||
        !dateValue ||
        !nightsIncrementer ||
        !nightsDecrementer ||
        !nightsValue
      ) {
        return;
      }

      flatpickr(dateValue, {
        altInput: true,
        altFormat: 'd M',
        altInputClass: '',
        dateFormat: 'Y-m-d',
        defaultDate: tomorrow,
        minDate: today,
        disableMobile: true,
        locale: {
          ...locales[locale]
        },
        onChange: () => this.getOptions(module),

        // Activate incrementers/decrementers only when flatpickr is fully initiated
        // To prevent bugs/crashes
        onReady: (selectedDates, dateStr, instance) => {
          // Increment date with a debounce (delay)
          // Remove disabled state when date is after today
          dateIncrementer.addEventListener('click', () => {
            const currentDate = instance.selectedDates[0];
            if (!currentDate) return;
            const nextDate = new Date(currentDate);
            nextDate.setDate(nextDate.getDate() + 1);
            instance.setDate(nextDate);

            if (nextDate.getDate() > today.getDate()) {
              dateDecrementer.removeAttribute('disabled');
            }

            this.onChangeDebounce(module);
          });

          // Decrement date with a debounce (delay)
          // Disable when date is today
          dateDecrementer.addEventListener('click', () => {
            const currentDate = instance.selectedDates[0];
            if (!currentDate || this.isToday(currentDate)) return;
            const prevDate = new Date(currentDate);
            prevDate.setDate(prevDate.getDate() - 1);
            instance.setDate(this.isToday(prevDate) ? today : prevDate);

            if (this.isToday(prevDate)) {
              dateDecrementer.setAttribute('disabled', 'true');
            }

            this.onChangeDebounce(module);
          });

          // Increment night with a debounce (delay)
          // Remove disabled state when nights is bigger than 1
          nightsIncrementer.addEventListener('click', () => {
            nightsValue.value = +nightsValue.value + 1;

            if (+nightsValue.value > 1) {
              nightsDecrementer.removeAttribute('disabled');
            }

            this.onChangeDebounce(module);
          });

          // Decrement night with a debounce (delay)
          // Disabled when nights is 1
          nightsDecrementer.addEventListener('click', () => {
            if (+nightsValue.value === 1) return;
            nightsValue.value = +nightsValue.value - 1;

            if (+nightsValue.value === 1) {
              nightsDecrementer.setAttribute('disabled', 'true');
            }

            this.onChangeDebounce(module);
          });

          // Get room options on first page load
          this.getOptions(module);
        }
      });
    });
  }
}

export default Iibe;
